import { Component, JSXElement, Show, onCleanup, onMount } from 'solid-js';
import { BtnClasses } from '../../types/common';
import Icon from '../Icon/Icon';
import { IconColor } from '../Icon/icon-color';
import type { IconSize } from '../Icon/icon-size';
import { MODUS_BUTTON_STYLESHEET } from '../../mc-custom-css/mc-button.css';
import { injectInRenderContext } from '../util';

export type ModusButtonColor = `${BtnClasses}`;
export type ButtonType = 'solid' | 'hollow' | 'outline';
export type ButtonVariant = 'filled' | 'outlined' | 'borderless';
export type ButtonSize =
  | 'extraSmall'
  | 'small'
  | 'medium'
  | 'large'
  | 'extraLarge';
export type IconPosition = 'left' | 'right' | 'dropdown';
export type ButtonState = 'default' | 'active' | 'checked';

export interface ButtonProps {
  // Content
  content?: JSXElement | string;
  children?: JSXElement | string;
  /** Optional second line below `content`/`children`. Enables stacked title + subtitle layout. */
  subtitle?: string;

  // Button appearance
  /** @deprecated Use `variant` instead. */
  type?: ButtonType;
  variant?: ButtonVariant;
  color?: ModusButtonColor;
  size?: ButtonSize;
  shape?: 'ellipse' | 'rectangle' | 'square' | 'circle';

  fullWidth?: boolean;

  // Icon support
  icon?: string;
  iconColor?: IconColor;
  iconSize?: IconSize;
  iconPosition?: IconPosition;
  iconOnly?: boolean;
  iconClass?: string;
  /** When true, injects Connect icon-font <link> on the built-in `icon` (default false). */
  includeFont?: boolean;
  /** When true (tc-button host), inner Icon loads Connect font inside the shadow boundary. */
  shadowDom?: boolean;
  imageSrc?: string;
  imageAlt?: string;
  /** When true (default), `imageSrc` renders as a circular avatar. Set false
   *  for square/SVG icons that shouldn't be clipped to a circle. */
  imageRounded?: boolean;

  // State
  disabled?: boolean;
  state?: ButtonState; // TODO: Not available in Modus - 'active' and 'checked' states

  // Behavior
  onClick?(): void; // Handler function passed from web component wrapper
  buttonType?: 'button' | 'submit' | 'reset';

  // Accessibility
  title?: string;
  ariaLabel?: string;
  'data-cy'?: string;

  // Additional classes
  class?: string;
  skipPropagation?: boolean;
  // Ref forwarding — receives the modus-wc-button element once mounted
  ref?(el: HTMLElement): void;
}

const Button: Component<ButtonProps> = (props): JSXElement => {
  let buttonRef: HTMLElement | undefined;

  onMount(() => {
    let cancelled = false;
    customElements.whenDefined('modus-wc-button').then(() => {
      if (cancelled || !buttonRef) return;
      injectInRenderContext(
        buttonRef,
        'mc-button-style',
        MODUS_BUTTON_STYLESHEET
      );
    });
    onCleanup(() => {
      cancelled = true;
    });
  });

  const getContent = (): JSXElement | string =>
    props.content || props.children || '';
  const getShape = (): string => props.shape || 'rectangle';
  const getColor = (): ModusButtonColor => props.color ?? BtnClasses.Primary;
  const getIconPosition = (): IconPosition => props.iconPosition || 'left';
  const getButtonType = (): 'button' | 'submit' | 'reset' =>
    props.buttonType || 'button';
  const getVariant = (): ButtonVariant => props.variant ?? 'filled';
  const getSkipPropagation = (): boolean => props.skipPropagation ?? false;

  const getModusSize = (): 'xs' | 'sm' | 'md' | 'lg' | 'xl' => {
    switch (props.size) {
      case 'extraSmall':
        return 'xs';
      case 'small':
        return 'sm';
      case 'large':
        return 'lg';
      case 'extraLarge':
        return 'xl';
      case 'medium':
      default:
        return 'md';
    }
  };

  const getIconColor = (): IconColor | undefined => {
    if (props.iconColor !== undefined) {
      return props.iconColor;
    }

    const variant = getVariant();
    const isBorderlessOrOutlined =
      variant === 'borderless' || variant === 'outlined';

    switch (getColor()) {
      case BtnClasses.Primary:
        return isBorderlessOrOutlined ? IconColor.Primary : IconColor.Inverse;
      case BtnClasses.Secondary:
        return IconColor.TrimbleGray;
      case BtnClasses.Tertiary:
        return IconColor.Tertiary;
      default:
        return undefined;
    }
  };

  const getIconSize = (): IconSize => {
    if (props.iconSize) {
      return props.iconSize;
    }

    switch (props.size) {
      case 'extraSmall':
        return 'icon-tiny';
      case 'small':
        return 'icon-small';
      case 'large':
        return 'icon-large';
      case 'medium':
      default:
        return 'icon-medium';
    }
  };

  const iconLoadsConnectFont = (): boolean =>
    !!(props.includeFont || props.shadowDom);

  const renderIcon = (): JSXElement => {
    if (props.imageSrc) {
      const imgClass =
        props.imageRounded === false
          ? 'tc-button-avatar-img tc-button-avatar-img--square'
          : 'tc-button-avatar-img';
      return (
        <img src={props.imageSrc} alt={props.imageAlt ?? ''} class={imgClass} />
      );
    }
    if (!props.icon) return null;

    return (
      <Icon
        icon={props.icon}
        color={getIconColor()}
        size={getIconSize()}
        customClass={props.iconClass}
        includeFont={iconLoadsConnectFont()}
      />
    );
  };

  const hasSubtitle = (): boolean =>
    typeof props.subtitle === 'string' && props.subtitle.trim().length > 0;

  const renderDropdownArrow = (): JSXElement => {
    if (getIconPosition() !== 'dropdown') return null;
    return (
      <Icon
        icon="tc-icon-arrow-drop-down"
        color={getIconColor()}
        size="icon-small"
        includeFont={iconLoadsConnectFont()}
        aria-hidden="true"
      />
    );
  };

  const hasContent = (): boolean => {
    const content = getContent();
    return typeof content === 'string' ? content.trim().length > 0 : !!content;
  };

  const getCustomClass = (): string => {
    const classes: string[] = [];

    if (props.class) {
      classes.push(props.class);
    }

    return classes.join(' ');
  };

  return (
    <modus-wc-button
      ref={(el: HTMLElement) => {
        buttonRef = el;
        props.ref?.(el);
      }}
      color={getColor()}
      variant={getVariant()}
      size={getModusSize()}
      shape={getShape() as unknown as 'rectangle' | 'square' | 'circle'}
      disabled={props.disabled}
      full-width={props.fullWidth}
      type={getButtonType()}
      pressed={props.state === 'active' || props.state === 'checked'}
      aria-pressed={
        props.state === 'active' || props.state === 'checked'
          ? 'true'
          : undefined
      }
      custom-class={getCustomClass()}
      aria-label={props.ariaLabel}
      /*
       * on:click (not onClick) forces Solid to call addEventListener directly on this
       * element instead of using its default delegated-event system (a single listener
       * at the app root that resolves handlers via event.target). When this button is
       * portaled into SR1 (tc-modus-navbar's shadow root), a click that bubbles out of
       * SR1 gets its event.target retargeted to the shadow host, so the delegated
       * listener can never resolve back to this element. A direct listener fires during
       * the normal bubble phase inside SR1, before any retargeting occurs.
       */
      on:click={(event: MouseEvent) => {
        if (!props.disabled) {
          if (getSkipPropagation()) {
            event.stopPropagation();
          }
          props.onClick?.();
        }
      }}
      attr:data-testid={props['data-cy'] || 'button'}
    >
      {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
      <Show when={props.iconOnly}>{renderIcon()}</Show>
      <Show when={!props.iconOnly}>
        <Show
          when={hasSubtitle()}
          fallback={
            <>
              {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
              {getIconPosition() === 'left' && renderIcon()}
              <Show when={hasContent()}>
                {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
                <span>{getContent()}</span>
              </Show>
              {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
              {getIconPosition() === 'right' && renderIcon()}
              {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
              {renderDropdownArrow()}
            </>
          }
        >
          <div class="mc-button-content mc-button-content--with-subtitle">
            {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
            {getIconPosition() === 'left' && renderIcon()}
            <span class="mc-button-text">
              <Show when={hasContent()}>
                {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
                <span class="mc-button-title">{getContent()}</span>
              </Show>
              <span class="mc-button-subtitle">{props.subtitle}</span>
            </span>
            {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
            {getIconPosition() === 'right' && renderIcon()}
          </div>
          {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
          {renderDropdownArrow()}
        </Show>
      </Show>
    </modus-wc-button>
  );
};

export default Button;
